import { waitForFirebaseUser } from "./firebaseAuthHelper";

const AI_BASE =
  process.env.REACT_APP_INTERIOR_AI_BASE ||
  "https://modularobjects.com/mailserver/api/interior/ai";

const configuredTimeoutMs = Number(
  process.env.REACT_APP_GUIDED_AI_TIMEOUT_MS || 300000
);

export const GUIDED_AI_TIMEOUT_MS =
  Number.isFinite(configuredTimeoutMs) && configuredTimeoutMs > 0
    ? configuredTimeoutMs
    : 300000;

const activePolls = new Map();

function createAbortError() {
  const error = new Error("AI job polling was cancelled.");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

function createAuthenticationError(message, cause) {
  const error = new Error(message);
  error.name = "AuthenticationError";
  error.code = "AUTHENTICATION_FAILED";
  error.cause = cause;
  return error;
}

async function authenticatedGuidedAiRequest(
  path,
  { method = "GET", body, signal } = {}
) {
  throwIfAborted(signal);

  let user;

  try {
    user = await waitForFirebaseUser();
  } catch (error) {
    throw createAuthenticationError(
      "Authentication failed. Please sign in with Firebase and try again.",
      error
    );
  }

  throwIfAborted(signal);

  if (!user || typeof user.getIdToken !== "function") {
    throw createAuthenticationError(
      "Authentication failed. No signed-in Firebase user is available."
    );
  }

  let token;

  try {
    token = await user.getIdToken();
  } catch (error) {
    throw createAuthenticationError(
      "Authentication failed. Could not obtain a Firebase ID token.",
      error
    );
  }

  if (!token) {
    throw createAuthenticationError(
      "Authentication failed. Firebase returned an empty ID token."
    );
  }

  throwIfAborted(signal);

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  if (method !== "GET" || body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  return fetch(`${AI_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
}

async function parseJson(response) {
  const data = await response.json().catch(() => null);

  if (response.status === 401 || response.status === 403) {
    const error = createAuthenticationError(
      "Authentication failed. Please sign in again and retry."
    );
    error.status = response.status;
    throw error;
  }

  if (!response.ok || data?.ok === false) {
    const error = new Error(
      data?.message || `Request failed: ${response.status}`
    );
    error.status = response.status;
    throw error;
  }

  return data;
}

async function getInteriorAiJobResponse(jobId, { signal } = {}) {
  const response = await authenticatedGuidedAiRequest(`/jobs/${jobId}`, {
    method: "GET",
    signal,
  });
  const data = await parseJson(response);

  return {
    job: data.item,
    responseMessage: data.message,
  };
}

export async function createInteriorAiJob(
  {
    jobType,
    prompt,
    inputJson = {},
    contextJson = {},
    projectId = null,
    priority = 100,
  },
  { signal } = {}
) {
  const response = await authenticatedGuidedAiRequest("/jobs", {
    method: "POST",
    signal,
    body: {
      jobType,
      prompt,
      inputJson,
      contextJson,
      projectId,
      priority,
    },
  });

  const data = await parseJson(response);
  return data.item;
}

export async function getInteriorAiJob(jobId, { signal } = {}) {
  const { job } = await getInteriorAiJobResponse(jobId, { signal });
  return job;
}

export async function cancelInteriorAiJob(jobId, { signal } = {}) {
  const response = await authenticatedGuidedAiRequest(
    `/jobs/${jobId}/cancel`,
    {
      method: "POST",
      signal,
    }
  );

  const data = await parseJson(response);
  return data.item;
}

function waitForDelay(delayMs, signal) {
  throwIfAborted(signal);

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);

    function handleAbort() {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", handleAbort);
      reject(createAbortError());
    }

    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

function createTerminalJobError(job, responseMessage) {
  const status = String(job?.status || "").toLowerCase();
  const message =
    job?.error_message ||
    job?.errorMessage ||
    job?.message ||
    responseMessage ||
    `AI job ${status || "failed"}.`;
  const error = new Error(message);

  error.job = job;
  error.jobStatus = status;
  return error;
}

async function pollInteriorAiJob(jobId, options) {
  const {
    intervalMs = 2000,
    timeoutMs = GUIDED_AI_TIMEOUT_MS,
    onTick,
    signal,
  } = options;
  const startedAt = Date.now();

  while (true) {
    throwIfAborted(signal);

    const { job, responseMessage } = await getInteriorAiJobResponse(jobId, {
      signal,
    });
    const status = String(job?.status || "").toLowerCase();

    onTick?.(job);

    if (status === "completed") {
      return job;
    }

    if (status === "failed" || status === "cancelled") {
      throw createTerminalJobError(job, responseMessage);
    }

    if (Date.now() - startedAt >= timeoutMs) {
      const error = new Error("AI job timed out.");
      error.code = "AI_JOB_TIMEOUT";
      throw error;
    }

    await waitForDelay(intervalMs, signal);
  }
}

export function waitForInteriorAiJob(jobId, options = {}) {
  const pollKey = String(jobId);
  const existingPoll = activePolls.get(pollKey);

  if (existingPoll) {
    return existingPoll;
  }

  const poll = pollInteriorAiJob(jobId, options).finally(() => {
    if (activePolls.get(pollKey) === poll) {
      activePolls.delete(pollKey);
    }
  });

  activePolls.set(pollKey, poll);
  return poll;
}
